import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ReportView,
  SchedulerReports,
  SchedulerReportView,
  ItemCatalog,
  ReferenceOption,
  SettingView,
  TemplateUsage,
} from "@fieldpath/shared";
import { mdyTime, syncHealth } from "@fieldpath/shared";

import {
  api,
  ApiError,
  type DeltekEntityRun,
  type DeltekExecution,
  type DeltekStatus,
  type Fetcher,
  type IvpReport,
  type PunchLabels,
} from "../api/client";
import { navigate, useSettledLoading } from "../router";
import { HelpButton } from "./HelpButton";
import { FieldAdminPanel } from "./FieldAdmin";
import { PeoplePicker } from "./PeoplePicker";

/**
 * The admin page: refresh Deltek, and see the estate's health.
 *
 * Who sees it is decided by the SERVER, from the admin flag on the Deltek
 * employee record. The route exists for everybody and every call returns 403
 * for anybody else — hiding the link would be security by obscurity, and the
 * rule belongs where it cannot be bypassed.
 *
 * **Every panel shows what is WRONG first and hides what is fine.** A page that
 * lists sixteen passing checks and three healthy entities in full is a page
 * people skim, and the one line that mattered is somewhere in the middle of it.
 *
 * The problem reports are the third panel and the only one that is READ ONLY
 * in the strong sense — there is no route that writes to them. **The punch
 * list is where they are triaged** (Jeremy, 27 Oct 2026; GitHub Issues was the
 * plan on 4 Oct): status, response and decision live there, so a bug has
 * exactly one record and it cannot drift out of step with itself. This is a
 * window on what people filed, not a tracker; for a punch-list member each
 * report names its row.
 */

const STATUS_LABEL: Record<string, string> = {
  pass: "Pass",
  warn: "Warn",
  fail: "Fail",
  unknown: "Unknown",
};

/** Matches the Exchange evidence rule inside the report itself. */
const IVP_STALE_HOURS = 24;
/** A reference sync runs daily, so a day and a half without one is a problem. */
const DELTEK_STALE_HOURS = 36;

function hoursSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round(((Date.now() - new Date(iso).getTime()) / 3_600_000) * 10) / 10;
}

/**
 * An actual date and time, in the reader's own timezone.
 *
 * **Replaced "2 hours ago" everywhere on this page**, at Jeremy's request,
 * 4 Oct 2026. Relative time is friendly and useless for the question this page
 * exists to answer: *did the thing I just did work?* "An hour ago" cannot be
 * compared against a log line, a Teams message or the moment somebody pressed
 * a button, and two things both reported as "an hour ago" may be forty minutes
 * apart.
 *
 * Times are stored UTC and rendered local, so this reads as Central without
 * anybody configuring anything — and it will read correctly for whoever is
 * somewhere else.
 */
function stamp(iso: string | null): string {
  if (!iso) return "never";
  return mdyTime(iso);
}

/** Thousands separators. 1048 reads as a typo; 1,048 reads as a number. */
function count(n: number): string {
  return n.toLocaleString();
}

/** How often to re-check while a run is in flight, and for how long. */
const POLL_MS = 8_000;
const POLL_LIMIT = 23; // ~3 minutes. A run takes about 35 seconds.

/** One line of a sync run: what it did, and to how much. */
function RunLine({ run }: { run: DeltekEntityRun }) {
  // "synced", not just "rows": this is what the RUN wrote, which is a
  // different number from how much reference data there is. Saying so here
  // stops the history contradicting the summary above it.
  const rows =
    run.outcome === "applied"
      ? `${count(run.rowsUpserted ?? 0)} rows synced` +
        (run.rowsMissing ? `, ${count(run.rowsMissing)} newly absent` : "")
      : null;

  return (
    <tr>
      <td className="ivp-status">
        <span className={`chip sync-${run.outcome}`}>{run.outcome}</span>
      </td>
      <td>
        <div className="ivp-title">{run.entity}</div>
        <div className="muted ivp-summary">
          {rows ? `${rows} · ` : ""}
          {stamp(run.finishedAt)}
          {/* A refusal's reason is the whole point of recording it. An applied
              run's detail is just the row count again, so it is left off. */}
          {run.outcome !== "applied" && run.detail ? ` — ${run.detail}` : ""}
        </div>
      </td>
    </tr>
  );
}

function DeltekPanel({
  fetcher,
  status,
  executions,
  onChanged,
}: {
  fetcher: Fetcher;
  status: DeltekStatus;
  executions: readonly DeltekExecution[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.refreshDeltek(fetcher);
      setMessage(result.message);
      onChanged();
    } catch (e) {
      // Verbatim. The server's refusals say a run is already going, or how long
      // is left on the cooldown — replacing that with "could not start" throws
      // away the part that tells you what to do.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const staleHours = hoursSince(status.lastRefreshAt);
  const stale = staleHours === null || staleHours > DELTEK_STALE_HOURS;
  const troubled = status.entities.filter((e) => e.outcome !== "applied");

  /*
   * What to say about the last run. The reasoning lives in `syncHealth` in
   * `shared/`, as a PURE FUNCTION, because this is where it was got wrong:
   * the warning used to fire on every successful refresh, since a run still
   * in progress has started more recently than anything recorded.
   */
  const health = syncHealth(executions, status.lastRefreshAt);

  /*
   * While a run is in flight, re-check until it finishes.
   *
   * Without this the page sits on the PREVIOUS run's numbers after the button
   * is pressed, and a person reasonably concludes the button did nothing. The
   * reload is quiet — no spinner — or the whole page would blink every eight
   * seconds. Bounded, so a job that hangs does not poll until the tab closes.
   */
  useEffect(() => {
    if (health.kind !== "running") return;
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > POLL_LIMIT) {
        clearInterval(timer);
        return;
      }
      onChanged();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [health.kind, onChanged]);

  return (
    <div className="card admin-panel">
      <div className="row spread">
        <h2>Deltek reference data<HelpButton screens="admin-deltek" /></h2>
        <button className="primary" onClick={() => void refresh()} disabled={busy}>
          {busy ? "Starting…" : "Refresh from Deltek"}
        </button>
      </div>

      <p className={stale && health.kind !== "running" ? "ivp-stale" : "muted"}>
        Last refreshed {stamp(status.lastRefreshAt)}
        {stale && health.kind !== "running"
          ? " — it runs daily at 06:00 UTC, so this is overdue."
          : "."}
      </p>

      {/* A run in flight is NOT a problem, and must not be dressed as one.
          It is also not evidence about the data yet, which is why it says
          nothing about outcomes. */}
      {health.kind === "running" ? (
        <p className="ivp-note">
          A refresh is running now, started {stamp(health.startedAt)}. It takes
          about half a minute; these numbers update by themselves when it finishes.
        </p>
      ) : null}

      {health.kind === "silent" ? (
        <p className="ivp-stale">
          A run started {stamp(health.startedAt)}, finished, and recorded nothing.
          It did not reach the database — check the job's logs.
        </p>
      ) : null}

      {message ? <p className="ivp-note">{message}</p> : null}
      {error ? <p className="error-inline">{error}</p> : null}

      {status.entities.length === 0 ? (
        <p className="muted">The sync has never run.</p>
      ) : (
        <table className="ivp-table">
          <tbody>
            {/* Anything that did not apply is shown whatever its age. The rest
                is one summary line, because three healthy entities listed in
                full is noise on a page you visit to find a problem. */}
            {troubled.map((run) => (
              <RunLine key={run.entity} run={run} />
            ))}
            {troubled.length < status.entities.length ? (
              <tr>
                <td className="ivp-status">
                  <span className="chip sync-applied">applied</span>
                </td>
                <td>
                  {/* ACTIVE first, every row in brackets.
                      "101 employees" read as an error to anybody who knows
                      the company has 36 people, and "1,048 projects" against
                      about 300 live jobs read as a worse one. Both numbers
                      were right and only one of them was the answer. */}
                  <div className="muted ivp-summary">
                    {status.counts.length > 0
                      ? status.counts
                          .map((c) => `${c.entity} ${count(c.active)} (${count(c.total)})`)
                          .join(" · ")
                      : status.entities
                          .filter((e) => e.outcome === "applied")
                          .map((e) => `${e.entity} ${count(e.rowsUpserted ?? 0)}`)
                          .join(" · ")}
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}

      {status.history.length > 0 ? (
        <>
          <button className="linkish expander" onClick={() => setShowHistory((v) => !v)}>
            {showHistory
              ? "Hide earlier runs"
              : `Show the last 7 days (${status.history.length} runs)`}
          </button>
          {showHistory ? (
            <table className="ivp-table">
              <tbody>
                {status.history.map((run, i) => (
                  <RunLine key={`${run.entity}-${run.finishedAt}-${i}`} run={run} />
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}

      <p className="muted admin-footnote">
        Counts are <strong>active</strong>, with every row in brackets. Deltek
        keeps everybody who has ever been on the payroll and every job ever
        opened, and it has to — work already references them — so the bracketed
        number is always the larger and is not a fault.
        <br />
        Refreshing starts the job; it does not guarantee new data. The sync refuses
        an extract that looks too short, on purpose — which is why these say
        applied or refused rather than succeeded.
      </p>
    </div>
  );
}

function IvpPanel({ report }: { report: IvpReport | null }) {
  const [showPasses, setShowPasses] = useState(false);

  if (!report) {
    return (
      <div className="card admin-panel">
        <h2>Estate health<HelpButton screens="admin-health" /></h2>
        <p className="muted">
          No IVP report has been recorded yet. Run{" "}
          <code>node infra/ivp/run.mjs --record</code> and it will appear here.
        </p>
        <p className="muted admin-footnote">
          Canopy shows the report; it does not run it. The checks test live
          resources as a person with administrative access, which is what makes
          them worth believing.
        </p>
      </div>
    );
  }

  const stale = report.ageHours > IVP_STALE_HOURS;
  const worst =
    report.failed > 0
      ? "fail"
      : report.unknown > 0
        ? "unknown"
        : report.warned > 0
          ? "warn"
          : "pass";

  // Everything that is not a pass, worst first — the server already ordered
  // them, so this preserves that rather than sorting again.
  const notable = report.findings.filter((f) => f.status !== "pass");
  const passes = report.findings.filter((f) => f.status === "pass");

  return (
    <div className="card admin-panel">
      <div className="row spread">
        <h2>Estate health<HelpButton screens="admin-health" /></h2>
        <span className={`chip ivp-${worst}`}>
          {report.passed} pass · {report.warned} warn · {report.failed} fail ·{" "}
          {report.unknown} unknown
        </span>
      </div>

      <p className={stale ? "ivp-stale" : "muted"}>
        Run {stamp(report.ranAt)}
        {report.ranBy ? ` by ${report.ranBy}` : ""}
        {stale ? " — old enough that it describes the estate as it WAS, not as it is." : "."}
      </p>

      {notable.length === 0 ? (
        <p className="muted">Everything passed.</p>
      ) : (
        <table className="ivp-table">
          <tbody>
            {notable.map((f) => (
              <tr key={f.checkId}>
                <td className="ivp-status">
                  <span className={`chip ivp-${f.status}`}>
                    {STATUS_LABEL[f.status] ?? f.status}
                  </span>
                </td>
                <td>
                  <div className="ivp-title">{f.title}</div>
                  <div className="muted ivp-summary">{f.summary}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {passes.length > 0 ? (
        <>
          <button className="linkish expander" onClick={() => setShowPasses((v) => !v)}>
            {showPasses
              ? "Hide the passing checks"
              : `Show the ${passes.length} passing checks`}
          </button>
          {showPasses ? (
            <table className="ivp-table">
              <tbody>
                {passes.map((f) => (
                  <tr key={f.checkId}>
                    <td className="ivp-status">
                      <span className="chip ivp-pass">Pass</span>
                    </td>
                    <td>
                      <div className="ivp-title">{f.title}</div>
                      <div className="muted ivp-summary">{f.summary}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * One report, collapsed to a line.
 *
 * `body` holds up to 4,000 characters and people paste stack traces into it —
 * which is the right thing for them to do and the wrong thing to render forty
 * of at once. The first line is almost always the sentence that says what
 * happened; everything else is behind a click.
 */
/**
 * One report, from EITHER application.
 *
 * Generalised rather than copied when the scheduler's reports arrived on 6 Oct
 * 2026. A second near-identical row component is two things that must keep
 * agreeing about how a report reads, and they would not — the two tables are
 * meant to be read side by side, so they have to look alike by construction.
 *
 * The only difference between the two shapes is `reporterEmail`, which the
 * scheduler carries and Fieldpath does not.
 */
function ReportRow({
  report,
  anchor,
  punch,
}: {
  report: ReportView | SchedulerReportView;
  /** `report-<id>` or `scheduler-report-<id>`: the punch list links here, and it opens. */
  anchor: string;
  /** Its punch-list row (F94), for a member; undefined for anybody else. */
  punch: string | undefined;
}) {
  const reporterEmail = "reporterEmail" in report ? report.reporterEmail : null;
  const linkedTo = window.location.hash === `#${anchor}`;
  const [open, setOpen] = useState(linkedTo);
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (linkedTo) rowRef.current?.scrollIntoView({ block: "center" });
  }, [linkedTo]);

  const body = report.body.trim();
  const firstLine = body.split("\n")[0]!.trim();
  const hasMore = firstLine.length < body.length;

  /*
   * The context the app captured for them. Labelled, because a bare "/tasks/
   * 9f2c" in a list means nothing — and absent is worth SAYING, since a report
   * with no reference is the ordinary case rather than a gap in the record.
   */
  const context = [
    report.route ? `on ${report.route}` : null,
    report.reference ? `after error ${report.reference}` : null,
    report.appBuild ? `build ${report.appBuild}` : null,
  ].filter(Boolean);

  return (
    <tr id={anchor} ref={rowRef} className={linkedTo ? "report-linked" : undefined}>
      <td className="report-kind">
        <span className="chip report-chip">{report.category}</span>
        {punch ? (
          <a
            className="report-punch"
            href={`/punchlist/${punch}`}
            title="Its row on the punch list"
            onClick={(e) => {
              // A plain click stays in the app; a modified one opens a new tab as usual.
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              navigate({ kind: "punchlist", label: punch });
            }}
          >
            {punch}
          </a>
        ) : null}
      </td>
      <td>
        {hasMore ? (
          <button
            className="linkish report-line"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {firstLine}
          </button>
        ) : (
          <div className="ivp-title">{firstLine}</div>
        )}

        <div className="muted ivp-summary">
          {/* Null is normal, not missing data: somebody with no Deltek record
              can still report a problem, and is exactly who hits a sign-in
              problem worth reporting. */}
          {/* The email is the whole point when there is no name: the scheduler
              accepts a report from somebody its roster cannot identify, and
              that person is the one most likely to be locked out and least
              able to chase it. Without an address the report is unanswerable. */}
          {report.reporterName ?? reporterEmail ?? "Someone with no Deltek record"} ·{" "}
          {stamp(report.createdAt)}
        </div>

        {open ? (
          <>
            <p className="report-body">{body}</p>
            <div className="muted ivp-summary">
              {context.length > 0
                ? context.join(" · ")
                : "No page or error was captured with this one."}
            </div>
          </>
        ) : null}
      </td>
    </tr>
  );
}

function ReportsPanel({
  reports,
  more,
  punch,
}: {
  reports: readonly ReportView[];
  more: boolean;
  punch: PunchLabels | null;
}) {
  // Collapsed by DEFAULT, unlike the other two panels. Those answer "is
  // anything wrong right now", which is worth reading on arrival. This is a
  // list you come to the page to read, so it opens when asked -- or when the
  // punch list linked to one of its reports.
  const [show, setShow] = useState(() => reports.some((r) => window.location.hash === `#report-${r.id}`));

  return (
    <div className="card admin-panel">
      <div className="row spread">
        <h2>Problem reports<HelpButton screens="admin-reports" /></h2>
        {reports.length > 0 ? (
          <span className="chip report-chip">
            {reports.length}
            {more ? "+" : ""} filed
          </span>
        ) : null}
      </div>

      {reports.length === 0 ? (
        <p className="muted">
          Nobody has reported a problem yet. The control is in the header on
          every screen, for everyone signed in.
        </p>
      ) : (
        <>
          <p className="muted">
            Newest first. The latest arrived {stamp(reports[0]!.createdAt)}.
          </p>

          <button className="linkish expander" onClick={() => setShow((v) => !v)}>
            {show ? "Hide the reports" : `Show the ${reports.length} reports`}
          </button>

          {show ? (
            <table className="ivp-table">
              <tbody>
                {reports.map((r) => (
                  <ReportRow key={r.id} report={r} anchor={`report-${r.id}`} punch={punch?.[r.id]} />
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      )}

      <p className="muted admin-footnote">
        {/* Saying where triage lives, on the screen itself, is what stops this
            panel turning into one. The absence of a status column is a
            decision, and a decision nobody can see gets reinvented. */}
        Triage lives on the punch list — this shows what people filed, and
        deliberately carries no status, so a bug has one record rather than two
        that must agree.
        {more
          ? " More have been filed than are shown here; infra/sql/bug-reports.sql exports every one."
          : ""}
      </p>
    </div>
  );
}

/**
 * The resource scheduler's problem reports.
 *
 * **A separate panel, not merged into the one above**, because the two can
 * fail independently. Merging them would mean the scheduler being mid-deploy
 * either hides Fieldpath's reports too or quietly shortens one list that still
 * looks complete.
 *
 * **The `available: false` branch is the reason this component exists.** Until
 * 6 Oct 2026 these reports had no screen at all, and two sat unread. The way to
 * rebuild that bug is to render a failure as an empty list — so the type is a
 * union and this function cannot forget to say which it got.
 */
function SchedulerReportsPanel({ state, punch }: { state: SchedulerReports | null; punch: PunchLabels | null }) {
  const [show, setShow] = useState(
    () => state?.available === true && state.reports.some((r) => window.location.hash === `#scheduler-report-${r.id}`),
  );

  return (
    <div className="card admin-panel">
      <div className="row spread">
        <h2>Problem reports — resource scheduler<HelpButton screens="admin-reports" /></h2>
        {state?.available && state.reports.length > 0 ? (
          <span className="chip report-chip">
            {state.reports.length}
            {state.more ? "+" : ""} filed
          </span>
        ) : null}
      </div>

      {state === null ? (
        <p className="muted">Reading them from the scheduler…</p>
      ) : !state.available ? (
        /* Never "no reports". This says the question could not be answered,
           which is a different fact and the one that needs acting on. */
        <p className="muted admin-warn">
          <strong>These could not be read.</strong> {state.reason}
        </p>
      ) : state.reports.length === 0 ? (
        <p className="muted">
          Nobody has reported a problem in the scheduler yet. Its control is in
          that application, on every screen.
        </p>
      ) : (
        <>
          <p className="muted">
            Newest first. The latest arrived {stamp(state.reports[0]!.createdAt)}.
          </p>

          <button className="linkish expander" onClick={() => setShow((v) => !v)}>
            {show ? "Hide the reports" : `Show the ${state.reports.length} reports`}
          </button>

          {show ? (
            <table className="ivp-table">
              <tbody>
                {state.reports.map((r) => (
                  <ReportRow key={r.id} report={r} anchor={`scheduler-report-${r.id}`} punch={punch?.[r.id]} />
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      )}

      <p className="muted admin-footnote">
        Filed in the scheduling side of Canopy and read over its API — this
        application never reads that one's tables directly. Reading them puts any
        new one on the punch list, which is where both apps' reports are triaged.
        {state?.available && state.more
          ? " More have been filed than are shown here."
          : ""}
      </p>
    </div>
  );
}

function TestScenariosPanel({ fetcher }: { fetcher: Fetcher }) {
  const [enabled, setEnabled] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ removedProjects: number; projects: number; tasks: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .testScenariosEnabled(fetcher)
      .then(setEnabled)
      .catch(() => setEnabled(false));
  }, [fetcher]);

  // Nothing at all unless the setting is on, which it is on dev alone.
  if (!enabled) return null;

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.loadTestScenarios(fetcher));
      setTyped("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Load test scenarios</h2>
      <p>
        Builds the starting point of every scenario in the click-through test plan, in
        projects named "UAT …". Pressing it again puts every scenario back to its
        starting point: <strong>every project whose name starts "UAT" is removed first</strong>,
        including any a tester made.
      </p>
      <p className="muted">Dev only. Production never shows this panel.</p>
      {error ? <p className="admin-warn">{error}</p> : null}
      {result ? (
        <p className="muted">
          Removed {result.removedProjects} old UAT project{result.removedProjects === 1 ? "" : "s"};
          created {result.projects} projects and {result.tasks} tasks.
        </p>
      ) : null}
      <div className="row">
        <input
          aria-label="Type LOAD to confirm"
          placeholder="Type LOAD to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <button type="button" className="primary" disabled={busy || typed !== "LOAD"} onClick={() => void run()}>
          {busy ? "Loading…" : "Load test scenarios"}
        </button>
      </div>
    </div>
  );
}

/**
 * The last 24 hours of activity in Canopy and the scheduler, as an Excel file
 * (Jeremy, 2 Nov 2026; his words for the heading, the line and the button).
 * Built on the server (`GET /api/admin/activity`) and saved from here.
 */
function ActivityPanel({ fetcher }: { fetcher: Fetcher }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { file, name } = await api.downloadActivity(fetcher);
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Activity in the last 24 hours<HelpButton screens="admin" /></h2>
      <p>Everything people changed in Canopy and the scheduler since this time yesterday. Times are Central.</p>
      {error ? <p className="admin-warn">{error}</p> : null}
      <div className="row">
        <button type="button" className="primary" disabled={busy} onClick={() => void run()}>
          Download the activity
        </button>
      </div>
    </div>
  );
}

/**
 * Training data (21 Oct 2026): DEV ONLY. Replaces every project's work and all
 * history with realistic work for real people, for the help articles and the
 * training. Typing the words is the confirmation, as for the other wipes.
 */
function TrainingDataPanel({ fetcher }: { fetcher: Fetcher }) {
  const [enabled, setEnabled] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ projects: number; tasks: number; linked: number; refused: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .trainingDataEnabled(fetcher)
      .then(setEnabled)
      .catch(() => setEnabled(false));
  }, [fetcher]);

  if (!enabled) return null;

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.loadTrainingData(fetcher));
      setTyped("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Load training data</h2>
      <p>
        <strong>Removes every project, task and all history on this environment</strong>, then
        builds realistic work for real ERP people: eight projects on real Ajera jobs, made from
        the templates, in every state the help and training need. Load the resource scheduler's
        training data first (its HR tab), so the field tasks can link to its deployments.
      </p>
      <p className="muted">Dev only. Production never shows this panel.</p>
      {error ? <p className="admin-warn">{error}</p> : null}
      {result ? (
        <div className="muted">
          <p>
            Created {result.projects} projects and {result.tasks} tasks; linked {result.linked} field
            task{result.linked === 1 ? "" : "s"} to the scheduler.
          </p>
          {result.refused.length ? (
            <ul>{result.refused.map((r) => <li key={r}>{r}</li>)}</ul>
          ) : null}
        </div>
      ) : null}
      <div className="row">
        <input
          aria-label="Type REPLACE ALL WORK to confirm"
          placeholder="Type REPLACE ALL WORK to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <button type="button" className="primary" disabled={busy || typed !== "REPLACE ALL WORK"} onClick={() => void run()}>
          {busy ? "Loading…" : "Load training data"}
        </button>
      </div>
    </div>
  );
}

/** How a stored value reads: "On", "20 business days", "Jeremy Toohey, Pam Manager". */
function shown(setting: SettingView, value: string, people: readonly ReferenceOption[] = []): string {
  if (setting.kind === "boolean") return value === "true" ? "On" : "Off";
  if (setting.kind === "people") {
    const keys = value === "" ? [] : value.split(",");
    if (keys.length === 0) return "Nobody";
    return keys.map((k) => people.find((p) => p.key === k)?.label ?? `Ajera ${k}`).join(", ");
  }
  if (setting.kind === "hours") return `${count(Number(value))} hour${value === "1" ? "" : "s"} a day`;
  if (setting.kind === "count") return `${count(Number(value))} time${value === "1" ? "" : "s"}`;
  return `${count(Number(value))} business day${value === "1" ? "" : "s"}`;
}

/**
 * One setting: what it is, its value, and a Save that appears only when the
 * draft differs. Each row saves on its own, as the plan grid does -- one bad
 * number must not throw away a good one beside it.
 */
function SettingRow({
  setting,
  fetcher,
  onSaved,
  people,
}: {
  setting: SettingView;
  fetcher: Fetcher;
  onSaved: () => void;
  /** Everybody, for a `people` setting's picker and names. */
  people: readonly ReferenceOption[];
}) {
  const [draft, setDraft] = useState(setting.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(setting.value), [setting.value]);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.updateSetting(fetcher, setting.key, draft);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const changed = setting.value !== setting.defaultValue;
  return (
    <div className="setting-row">
      <div className="setting-what">
        <strong>{setting.label}</strong>
        <p className="muted">{setting.description}</p>
        <p className="setting-meta">
          {changed ? (
            <>
              <span className="setting-changed">CHANGED</span> from{" "}
              {shown(setting, setting.defaultValue, people)}
              {setting.updatedByName
                ? ` by ${setting.updatedByName}, ${stamp(setting.updatedAt)}`
                : ""}
            </>
          ) : (
            "At its default"
          )}
        </p>
        {error ? <p className="error-inline">{error}</p> : null}
      </div>
      <div className="setting-control">
        {setting.kind === "people" ? (
          <div className="setting-people">
            <PeoplePicker
              label={`Now: ${shown(setting, setting.value, people)}`}
              people={[...people]}
              selected={draft === "" ? [] : draft.split(",")}
              onChange={(keys) => setDraft([...keys].sort((a, b) => Number(a) - Number(b)).join(","))}
            />
          </div>
        ) : setting.kind === "boolean" ? (
          <select
            aria-label={setting.label}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          >
            <option value="true">On</option>
            <option value="false">Off</option>
          </select>
        ) : (
          <div className="setting-days">
            <input
              aria-label={setting.label}
              type="number"
              min={setting.min ?? undefined}
              max={setting.max ?? undefined}
              step={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <span className="muted">{setting.kind === "hours" ? "hours a day" : setting.kind === "count" ? "times" : "business days"}</span>
          </div>
        )}
        <button
          className="primary"
          disabled={busy || draft.trim() === setting.value}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/**
 * THE SETTINGS PANEL -- PART 3 app 8 / PART 5 app 9, one screen.
 *
 * Leads with what has been CHANGED from its default and collapses the rest,
 * as every panel here leads with what is wrong: a page listing every setting
 * in full is one people skim, which is how a wrong one survives. What each
 * key accepts is the server's registry; this only offers the matching control.
 */
function SettingsPanel({ fetcher }: { fetcher: Fetcher }) {
  const [settings, setSettings] = useState<readonly SettingView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDefaults, setShowDefaults] = useState(false);
  const [people, setPeople] = useState<ReferenceOption[]>([]);
  useEffect(() => {
    void api.employees(fetcher).then(setPeople).catch(() => undefined);
  }, [fetcher]);

  const load = useCallback(() => {
    void api
      .settings(fetcher)
      .then((s) => {
        setSettings(s);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);
  useEffect(load, [load]);

  if (error) {
    return (
      <div className="card admin-panel">
        <h2>Settings<HelpButton screens="admin-settings" /></h2>
        <p className="admin-warn">The settings could not be read: {error}</p>
      </div>
    );
  }
  if (!settings) return null;

  const changed = settings.filter((s) => s.value !== s.defaultValue);
  const atDefault = settings.filter((s) => s.value === s.defaultValue);

  return (
    <div className="card admin-panel">
      <h2>Settings<HelpButton screens="admin-settings" /></h2>
      {changed.length === 0 ? (
        <p className="muted">Every setting is at its default.</p>
      ) : (
        changed.map((s) => (
          <SettingRow key={s.key} setting={s} fetcher={fetcher} onSaved={load} people={people} />
        ))
      )}
      {atDefault.length > 0 ? (
        <>
          <button className="linkish expander" onClick={() => setShowDefaults((v) => !v)}>
            {showDefaults
              ? "Hide the settings at their defaults"
              : `Show the ${atDefault.length} setting${atDefault.length === 1 ? "" : "s"} at ${
                  atDefault.length === 1 ? "its default" : "their defaults"
                }`}
          </button>
          {showDefaults
            ? atDefault.map((s) => (
                <SettingRow key={s.key} setting={s} fetcher={fetcher} onSaved={load} people={people} />
              ))
            : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * One template: what it is, what uses it, and the switch. Switching OFF a
 * task template that deliverable templates list asks once more, saying what
 * will happen -- those deliverables will skip it when work is added (E2.14).
 */
function TemplateRow({
  name,
  templateKey,
  detail,
  isActive,
  warnOff,
  onSet,
  extra = null,
}: {
  name: string;
  templateKey: string;
  detail: string;
  isActive: boolean;
  /** Said before switching off, when it matters; null when it does not. */
  warnOff: string | null;
  onSet: (isActive: boolean) => Promise<void>;
  /** Anything more the row carries -- a task template's "Offer on its own". */
  extra?: React.ReactNode;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (next: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onSet(next);
      setConfirming(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`template-row ${isActive ? "" : "is-off"}`}>
      <div className="template-what">
        <span className="template-name">{name}</span>
        <span className="template-key">{templateKey}</span>
        {isActive ? null : <span className="template-off">OFF</span>}
        <p className="muted">{detail}</p>
        {extra}
        {confirming && warnOff ? <p className="admin-warn">{warnOff}</p> : null}
        {error ? <p className="error-inline">{error}</p> : null}
      </div>
      <div className="template-control">
        {isActive && confirming ? (
          <>
            <button onClick={() => setConfirming(false)} disabled={busy}>
              Keep it on
            </button>
            <button className="destructive" onClick={() => void set(false)} disabled={busy}>
              Switch off
            </button>
          </>
        ) : (
          <button
            className="secondary"
            disabled={busy}
            onClick={() =>
              isActive && warnOff ? setConfirming(true) : void set(!isActive)
            }
          >
            {busy ? "Saving…" : isActive ? "Switch off" : "Switch on"}
          </button>
        )}
      </div>
    </div>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * THE TEMPLATE LIST (E2 7a): every template, whether it is offered, and what
 * uses it. The workbook owns a template's structure; this page owns only the
 * active flag, so the two never write the same column. Switched-off
 * templates lead each list, because those are what change what people are
 * offered; the whole list is collapsed until asked for.
 */
function TemplatesPanel({ fetcher }: { fetcher: Fetcher }) {
  const [usage, setUsage] = useState<TemplateUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    void api
      .templateUsage(fetcher)
      .then((u) => {
        setUsage(u);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);
  useEffect(load, [load]);

  if (error) {
    return (
      <div className="card admin-panel">
        <h2>Templates<HelpButton screens="admin-templates" /></h2>
        <p className="admin-warn">The templates could not be read: {error}</p>
      </div>
    );
  }
  if (!usage) return null;

  const offFirst = <T extends { isActive: boolean; name: string }>(xs: readonly T[]) =>
    [...xs].sort((a, b) => Number(a.isActive) - Number(b.isActive) || a.name.localeCompare(b.name));
  const off =
    usage.deliverableTemplates.filter((d) => !d.isActive).length +
    usage.taskTemplates.filter((t) => !t.isActive).length;
  const set = async (kind: "task" | "deliverable", id: string, isActive: boolean) => {
    await api.setTemplateActive(fetcher, kind, id, isActive);
    load();
  };

  return (
    <div className="card admin-panel">
      <h2>Templates<HelpButton screens="admin-templates" /></h2>
      {/* Muted either way: switching one off is a decision, not a fault. */}
      <p className="muted">
        {plural(usage.deliverableTemplates.length, "deliverable template")} and{" "}
        {plural(usage.taskTemplates.length, "task template")}
        {off > 0 ? ` — ${off} switched off, so not offered.` : ", all offered."} Their
        content comes from the template workbook; here they are switched on and off, and a
        task template can be offered on its own as well as inside its deliverable.
      </p>
      <button className="linkish expander" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide the templates" : "Show the templates"}
      </button>
      {open ? (
        <>
          <h3 className="admin-subhead">Deliverable templates</h3>
          {offFirst(usage.deliverableTemplates).map((d) => (
            <TemplateRow
              key={d.id}
              name={d.name}
              templateKey={d.key}
              detail={plural(d.taskTemplates, "task template")}
              isActive={d.isActive}
              warnOff={null}
              onSet={(next) => set("deliverable", d.id, next)}
            />
          ))}
          <h3 className="admin-subhead">Task templates</h3>
          {offFirst(usage.taskTemplates).map((t) => (
            <TemplateRow
              key={t.id}
              name={t.name}
              templateKey={t.key}
              detail={`${t.workflowName} · in ${plural(
                t.usedByDeliverableTemplates,
                "deliverable template",
              )} · made ${plural(t.tasksMade, "task")}`}
              isActive={t.isActive}
              warnOff={
                t.usedByDeliverableTemplates > 0
                  ? `${plural(t.usedByDeliverableTemplates, "deliverable template")} list${
                      t.usedByDeliverableTemplates === 1 ? "s" : ""
                    } it, and will skip it when work is added from ${
                      t.usedByDeliverableTemplates === 1 ? "it" : "them"
                    }. Tasks already made are not touched.`
                  : null
              }
              onSet={(next) => set("task", t.id, next)}
              extra={
                <OfferAlone
                  partOf={t.usedByDeliverableTemplates}
                  offerAlone={t.offerAlone}
                  onSet={async (next) => {
                    await api.setTemplateOfferAlone(fetcher, t.id, next);
                    load();
                  }}
                />
              }
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

/**
 * Whether "Add from templates" offers a task template under "Tasks on their
 * own" (13 Oct 2026). One in no deliverable template always is -- that is what
 * it was built for; one that is a PART of a deliverable is offered inside it
 * unless this is ticked.
 */
function OfferAlone({
  partOf,
  offerAlone,
  onSet,
}: {
  partOf: number;
  offerAlone: boolean;
  onSet: (offerAlone: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (partOf === 0) {
    return <p className="muted template-offer">Offered on its own: it is part of no deliverable template.</p>;
  }
  return (
    <>
      <label className="template-offer">
        <input
          type="checkbox"
          checked={offerAlone}
          disabled={busy}
          onChange={async (e) => {
            setBusy(true);
            setError(null);
            try {
              await onSet(e.target.checked);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            } finally {
              setBusy(false);
            }
          }}
        />
        Also offer under &ldquo;Tasks on their own&rdquo; in Add from templates
      </label>
      {error ? <p className="error-inline">{error}</p> : null}
    </>
  );
}

/**
 * One catalog entry: its name (editable in place), how often it has been
 * used, and the switch. Renaming changes what is offered next, never an item
 * a task already carries -- those copied their name.
 */
function CatalogEntry({
  entry,
  onRename,
  onSet,
}: {
  entry: ItemCatalog["lists"][number]["types"][number];
  onRename: (name: string) => Promise<void>;
  onSet: (isActive: boolean) => Promise<void>;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(entry.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setRenaming(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`template-row ${entry.isActive ? "" : "is-off"}`}>
      <div className="template-what">
        {renaming ? (
          <div className="row" style={{ gap: 8 }}>
            <input
              aria-label={`New name for ${entry.name}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            <button
              className="primary"
              disabled={busy || name.trim() === "" || name.trim() === entry.name}
              onClick={() => void run(() => onRename(name))}
            >
              Save
            </button>
            <button onClick={() => { setRenaming(false); setName(entry.name); }} disabled={busy}>
              Cancel
            </button>
          </div>
        ) : (
          <>
            <span className="template-name">{entry.name}</span>
            {entry.isActive ? null : <span className="template-off">OFF</span>}
          </>
        )}
        <p className="muted">
          {entry.used === 0 ? "Not used yet" : `Used on ${entry.used} item${entry.used === 1 ? "" : "s"}`}
        </p>
        {error ? <p className="error-inline">{error}</p> : null}
      </div>
      <div className="template-control">
        {renaming ? null : (
          <button className="secondary" onClick={() => setRenaming(true)} disabled={busy}>
            Rename
          </button>
        )}
        <button className="secondary" onClick={() => void run(() => onSet(!entry.isActive))} disabled={busy}>
          {entry.isActive ? "Switch off" : "Switch on"}
        </button>
      </div>
    </div>
  );
}

/**
 * THE ITEM CATALOG (E1, PART 5 application row 3): what each stage list
 * offers -- the figure templates GIS draws, the record types that get
 * ordered. Figure templates start empty and are filled here (E1.10). A
 * person can always type a custom item, so an empty list never stops work.
 */
function ItemCatalogPanel({ fetcher }: { fetcher: Fetcher }) {
  const [catalog, setCatalog] = useState<ItemCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState<Record<string, string>>({});
  const [addError, setAddError] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    void api
      .itemCatalog(fetcher)
      .then((c) => {
        setCatalog(c);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);
  useEffect(load, [load]);

  if (error) {
    return (
      <div className="card admin-panel">
        <h2>Item lists<HelpButton screens="admin-item-lists" /></h2>
        <p className="admin-warn">The item lists could not be read: {error}</p>
      </div>
    );
  }
  if (!catalog) return null;

  const add = async (listKey: string): Promise<void> => {
    try {
      await api.addItemType(fetcher, listKey, adding[listKey] ?? "");
      setAdding((a) => ({ ...a, [listKey]: "" }));
      setAddError((a) => ({ ...a, [listKey]: "" }));
      load();
    } catch (e) {
      setAddError((a) => ({ ...a, [listKey]: e instanceof ApiError ? e.message : String(e) }));
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Item lists<HelpButton screens="admin-item-lists" /></h2>
      <p className="muted">
        {catalog.lists
          .map((l) => `${l.name}: ${l.types.filter((t) => t.isActive).length} offered`)
          .join(" · ")}
        . What a stage's list offers to choose from; anybody can still type a custom item.
      </p>
      <button className="linkish expander" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide the item lists" : "Show the item lists"}
      </button>
      {open
        ? catalog.lists.map((list) => (
            <div key={list.key}>
              <h3 className="admin-subhead">{list.name}</h3>
              {list.types.length === 0 ? (
                <p className="muted">Nothing on this list yet.</p>
              ) : (
                [...list.types]
                  .sort((a, b) => Number(a.isActive === false) - Number(b.isActive === false))
                  .map((t) => (
                    <CatalogEntry
                      key={t.id}
                      entry={t}
                      onRename={async (name) => {
                        await api.updateItemType(fetcher, t.id, { name });
                        load();
                      }}
                      onSet={async (isActive) => {
                        await api.updateItemType(fetcher, t.id, { isActive });
                        load();
                      }}
                    />
                  ))
              )}
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <input
                  aria-label={`Add to ${list.name}`}
                  placeholder={`Add to ${list.name}`}
                  value={adding[list.key] ?? ""}
                  onChange={(e) => setAdding((a) => ({ ...a, [list.key]: e.target.value }))}
                  style={{ maxWidth: 320 }}
                />
                <button
                  className="secondary"
                  disabled={(adding[list.key] ?? "").trim() === ""}
                  onClick={() => void add(list.key)}
                >
                  Add
                </button>
              </div>
              {addError[list.key] ? <p className="error-inline">{addError[list.key]}</p> : null}
            </div>
          ))
        : null}
    </div>
  );
}

/**
 * THE WORK DATA WIPE: the training reset between sessions (Jeremy, 22 Oct
 * 2026) and the go-live wipe on 8 Nov (E10.3), one tool. Shown ONLY while the
 * App Service carries its switch -- set (and the app restarted) for a session
 * or the day, removed after. It counts the work tables before anything is
 * pressed, takes a typed confirmation, and shows the counts after so they can
 * be checked; the panel then shows the new counts, ready for the next session.
 */
function GoLiveWipePanel({ fetcher }: { fetcher: Fetcher }) {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ before: Record<string, number>; after: Record<string, number> } | null>(null);

  useEffect(() => {
    void api
      .goLiveWipe(fetcher)
      .then((r) => setCounts(r.enabled ? (r.counts ?? {}) : null))
      .catch(() => setCounts(null));
  }, [fetcher]);

  if (!counts) return null;
  const total = (c: Record<string, number>) => Object.values(c).reduce((a, b) => a + b, 0);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.runGoLiveWipe(fetcher, typed);
      setResult(r);
      setCounts(r.after);
      setTyped("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Wipe the work data</h2>
      <p>
        Deletes every project except the template project — with every deliverable, task and
        everything under them — and all history: notes, notifications, date requests and the
        activity log. Workflows, templates, lists, field definitions, settings and people are kept.
      </p>
      <p>
        <strong>The template project is kept</strong>, with its deliverables, tasks, stages, people
        and instructions; its history goes with the rest. Use this to start each training session
        clean, and once more at go-live on 8 Nov. It can be run as often as needed.
      </p>
      <p className="admin-warn">
        This cannot be undone from here; before go-live, take a database backup first. In the work
        tables now: {count(total(counts))} rows —{" "}
        {Object.entries(counts).filter(([, n]) => n > 0).map(([t, n]) => `${t} ${count(n)}`).join(", ") || "none"}.
        The template project's rows are among them and stay.
      </p>
      {error ? <p className="admin-warn">{error}</p> : null}
      {result ? (
        <p className="muted">
          Removed {count(total(result.before) - total(result.after) + 1)} rows. Left:{" "}
          {count(total(result.after))} — the template project's, and the one activity row recording
          this wipe.
        </p>
      ) : null}
      <div className="row">
        <input
          aria-label="Type WIPE ALL WORK to confirm"
          placeholder="Type WIPE ALL WORK to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <button
          type="button"
          className="destructive"
          disabled={busy || typed !== "WIPE ALL WORK"}
          onClick={() => void run()}
        >
          {busy ? "Wiping…" : "Wipe the work data"}
        </button>
      </div>
    </div>
  );
}

/**
 * THE WRIKE IMPORT (1 Nov 2026): one time, the work still open in Wrike
 * (api db/wrike-import.ts). Shown ONLY while the App Service carries its
 * switch, like the wipe. Its words are Jeremy's, exactly; the problems listed
 * under the result are the server's, one per item.
 */
function WrikeImportPanel({ fetcher }: { fetcher: Fetcher }) {
  const [enabled, setEnabled] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.runWrikeImport>> | null>(null);
  // The Wrike stages (1 Nov 2026): people and dates onto the imported tasks.
  const [typedStages, setTypedStages] = useState("");
  const [stages, setStages] = useState<Awaited<ReturnType<typeof api.runWrikeStages>> | null>(null);

  useEffect(() => {
    void api
      .wrikeImport(fetcher)
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, [fetcher]);

  if (!enabled) return null;

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.runWrikeImport(fetcher, typed));
      setTyped("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const runStages = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setStages(await api.runWrikeStages(fetcher, typedStages));
      setTypedStages("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card admin-panel">
      <h2>Wrike import</h2>
      <p>
        Loads 42 projects, 38 deliverables and 55 tasks from the Wrike import sheet (1 Nov 2026).
        Every task starts at its first stage. A project that already exists is skipped.
      </p>
      {error ? <p className="admin-warn">{error}</p> : null}
      {result ? (
        <p className="muted">
          Created {result.projectsCreated} projects, {result.deliverables} deliverables and{" "}
          {result.tasks} tasks. Skipped {result.projectsSkipped}.
        </p>
      ) : null}
      {result && result.problems.length > 0 ? (
        <ul className="admin-warn" style={{ listStyle: "none" }}>
          {result.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      <div className="row">
        <input
          aria-label="Type LOAD WRIKE to confirm"
          placeholder="Type LOAD WRIKE to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <button
          type="button"
          className="primary"
          disabled={busy || typed !== "LOAD WRIKE"}
          onClick={() => void run()}
        >
          Load the Wrike import
        </button>
      </div>

      <h3 style={{ marginTop: 24 }}>Wrike stages</h3>
      <p>
        Puts the people and due dates from the open Wrike tasks (1 Nov 2026) on 80 stages of the
        imported tasks. People are added, never taken off; the Wrike date replaces the stage's date.
        Nobody is notified.
      </p>
      {stages ? (
        <p className="muted">
          Updated {stages.stagesUpdated} stages: {stages.peopleAdded} people added, {stages.datesSet} dates
          set. Already done: {stages.unchanged}.
        </p>
      ) : null}
      {stages && stages.problems.length > 0 ? (
        <ul className="admin-warn" style={{ listStyle: "none" }}>
          {stages.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      <div className="row">
        <input
          aria-label="Type LOAD WRIKE STAGES to confirm"
          placeholder="Type LOAD WRIKE STAGES to confirm"
          value={typedStages}
          onChange={(e) => setTypedStages(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <button
          type="button"
          className="primary"
          disabled={busy || typedStages !== "LOAD WRIKE STAGES"}
          onClick={() => void runStages()}
        >
          Load the Wrike stages
        </button>
      </div>
    </div>
  );
}

export function Admin({ fetcher }: { fetcher: Fetcher }) {
  const [report, setReport] = useState<IvpReport | null>(null);
  const [deltek, setDeltek] = useState<DeltekStatus | null>(null);
  const [executions, setExecutions] = useState<readonly DeltekExecution[]>([]);
  const [reports, setReports] = useState<readonly ReportView[]>([]);
  const [moreReports, setMoreReports] = useState(false);
  const [punch, setPunch] = useState<PunchLabels | null>(null);
  const [schedulerPunch, setSchedulerPunch] = useState<PunchLabels | null>(null);
  // null means "not asked yet". The union covers read and could-not-read;
  // this third state is the moment before either is known.
  const [schedulerReports, setSchedulerReports] =
    useState<SchedulerReports | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * `quiet` skips the page-level loading state. The first load wants the
   * spinner; a poll every eight seconds must not blank a page somebody is
   * reading.
   */
  const load = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      // Independent, so they go together rather than one after another -- the
      // same reason the task page stopped making six round trips in series.
      const [ivp, del, filed, schedulerFiled] = await Promise.all([
        api.ivpReport(fetcher),
        api.deltekStatus(fetcher),
        api.problemReports(fetcher),
        // Cannot reject: the API turns every failure into
        // { available: false, reason }. A catch here would be a second place
        // for a failure to become an empty panel.
        api.schedulerProblemReports(fetcher),
      ]);
      setReport(ivp);
      setDeltek(del.status);
      setExecutions(del.executions);
      setReports(filed.reports);
      setMoreReports(filed.more);
      setPunch(filed.punch);
      setSchedulerReports(schedulerFiled);
      setSchedulerPunch(schedulerFiled.punch ?? null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setDenied(true);
      else setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  // Stable identity, so the poll's effect does not tear down and rebuild its
  // interval on every render.
  const reload = useCallback(() => void load(true), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner = useSettledLoading(loading);

  if (denied) {
    return (
      <div className="card">
        <h1>Admin</h1>
        <p>
          This page is for administrators. Access follows the admin flag on your
          Deltek employee record — ask whoever maintains Deltek if you need it.
        </p>
      </div>
    );
  }

  if (showSpinner) return <p className="muted">Loading…</p>;
  if (loading) return null;

  return (
    <>
      <h1>
        Admin
        <HelpButton screens="admin" />
      </h1>
      {error ? <p className="error-inline">{error}</p> : null}
      {deltek ? (
        <DeltekPanel
          fetcher={fetcher}
          status={deltek}
          executions={executions}
          onChanged={reload}
        />
      ) : null}
      <IvpPanel report={report} />
      {/* After the two "is anything wrong" panels, before the lists: it
          leads with what has been changed, which is the next thing an admin
          needs to know. */}
      <ActivityPanel fetcher={fetcher} />
      <SettingsPanel fetcher={fetcher} />
      <TemplatesPanel fetcher={fetcher} />
      <ItemCatalogPanel fetcher={fetcher} />
      <FieldAdminPanel fetcher={fetcher} />
      {/* Last, and collapsed. The two above answer "is anything wrong right
          now"; this one is a list to read, and it must not push the refresh
          button off the top of the screen. */}
      <ReportsPanel reports={reports} more={moreReports} punch={punch} />
      <SchedulerReportsPanel state={schedulerReports} punch={schedulerPunch} />
      {/* Last of all: rarely used, and each shows only while its switch is on. */}
      <TrainingDataPanel fetcher={fetcher} />
      <TestScenariosPanel fetcher={fetcher} />
      <WrikeImportPanel fetcher={fetcher} />
      <GoLiveWipePanel fetcher={fetcher} />
    </>
  );
}
